import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Grid, Typography } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import ReplayIcon from "@material-ui/icons/Replay";
import {
  FONT_OPTIONS,
  DEFAULT_FONT_ID,
  applyFont,
  getFontStack,
  getStoredFont,
} from "./M_FontSelector.helper";
import {
  DISPLAY_SETTINGS,
  DISPLAY_EVENT,
  getStoredValues,
  applyDisplaySetting,
  resetDisplaySettings,
} from "../M_DisplaySettings/M_DisplaySettings.helper";
import "./M_FontSelector.css";
import "../M_DisplaySettings/M_DisplaySettings.css";

const STRINGS = {
  HERO_TITLE: "Font & display",
  HERO_SUBTITLE:
    "Pick a typeface, then fine-tune text size, spacing and corners. Applies across the whole workspace.",
  RESET: "Reset to defaults",
  FONT_SECTION_TITLE: "Font family",
  FONT_SECTION_DESC: "Used for all text. System Default is the safest choice.",
  PREVIEW_LABEL: "Live preview",
  PREVIEW_HEADING: "Portfolio overview",
  PREVIEW_BODY:
    "The quick brown fox jumps over the lazy dog while reviewing 1,234 exposures.",
  PREVIEW_PRIMARY: "Primary action",
  PREVIEW_SECONDARY: "Secondary",
  ACTIVE: "Active",
  SAMPLE_GLYPH: "Ag",
};

const optionPreviewStyle = (settingKey, value) => {
  switch (settingKey) {
    case "fontScale":
      return { fontSize: `${0.78 * Number(value)}rem` };
    case "radiusScale":
      return { borderRadius: `${4 + 8 * Number(value)}px` };
    default:
      return undefined;
  }
};

const M_FontSelector = (props) => {
  const { onFontChange } = props;
  const [activeFont, setActiveFont] = useState(getStoredFont());
  const [displayValues, setDisplayValues] = useState(getStoredValues());

  useEffect(() => {
    setActiveFont(getStoredFont());
    setDisplayValues(getStoredValues());

    const handleFont = (event) => {
      const { font } = event.detail || {};
      if (font) {
        setActiveFont(font);
      }
    };
    const handleDisplay = (event) => {
      const { key, value } = event.detail || {};
      if (key) {
        setDisplayValues((prev) => ({ ...prev, [key]: value }));
      }
    };
    window.addEventListener("fontChange", handleFont);
    window.addEventListener(DISPLAY_EVENT, handleDisplay);
    return () => {
      window.removeEventListener("fontChange", handleFont);
      window.removeEventListener(DISPLAY_EVENT, handleDisplay);
    };
  }, []);

  const handleSelectFont = (fontId) => {
    if (!fontId || fontId === activeFont) {
      return;
    }
    setActiveFont(fontId);
    applyFont(fontId);
    if (typeof onFontChange === "function") {
      onFontChange(fontId);
    }
  };

  const handleSelectDisplay = (settingKey, value) => {
    if (displayValues[settingKey] === value) {
      return;
    }
    applyDisplaySetting(settingKey, value);
    setDisplayValues((prev) => ({ ...prev, [settingKey]: value }));
  };

  const handleReset = () => {
    resetDisplaySettings();
    applyFont(DEFAULT_FONT_ID);
    setActiveFont(DEFAULT_FONT_ID);
    setDisplayValues(getStoredValues());
  };

  const activeStack = getFontStack(activeFont);

  return (
    <div className="m-display">
      <div className="m-display__hero">
        <div className="m-display__hero-icon">
          <TextFieldsIcon />
        </div>
        <div className="m-display__hero-copy">
          <Typography className="m-display__hero-title">
            {STRINGS.HERO_TITLE}
          </Typography>
          <Typography className="m-display__hero-subtitle">
            {STRINGS.HERO_SUBTITLE}
          </Typography>
        </div>
        <button
          type="button"
          className="m-display__reset"
          onClick={handleReset}
          aria-label={STRINGS.RESET}
          title={STRINGS.RESET}
        >
          <ReplayIcon fontSize="small" />
          <span>{STRINGS.RESET}</span>
        </button>
      </div>

      <div className="m-display__preview" style={{ fontFamily: activeStack }}>
        <Typography className="m-display__preview-label">
          {STRINGS.PREVIEW_LABEL}
        </Typography>
        <Typography className="m-display__preview-heading">
          {STRINGS.PREVIEW_HEADING}
        </Typography>
        <Typography className="m-display__preview-body">
          {STRINGS.PREVIEW_BODY}
        </Typography>
        <div className="m-display__preview-actions">
          <span className="m-display__preview-btn">
            {STRINGS.PREVIEW_PRIMARY}
          </span>
          <span className="m-display__preview-btn m-display__preview-btn--outline">
            {STRINGS.PREVIEW_SECONDARY}
          </span>
        </div>
      </div>

      <div className="m-display__section">
        <div className="m-display__section-head">
          <Typography className="m-display__section-title">
            {STRINGS.FONT_SECTION_TITLE}
          </Typography>
          <Typography className="m-display__section-desc">
            {STRINGS.FONT_SECTION_DESC}
          </Typography>
        </div>
        <Grid container spacing={2} className="m-font-selector__grid">
          {FONT_OPTIONS.map((font) => {
            const isActive = activeFont === font.value;
            const cardClassName =
              "m-font-selector__card" +
              (isActive ? " m-font-selector__card--active" : "");

            return (
              <Grid item xs={12} sm={4} md={3} key={font.value}>
                <Box
                  className={cardClassName}
                  onClick={() => handleSelectFont(font.value)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelectFont(font.value);
                    }
                  }}
                  aria-pressed={isActive}
                >
                  {isActive ? (
                    <span
                      className="m-font-selector__check-badge"
                      aria-label={STRINGS.ACTIVE}
                      title={STRINGS.ACTIVE}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </span>
                  ) : null}
                  <span
                    className="m-font-selector__sample"
                    style={{ fontFamily: getFontStack(font.value) }}
                  >
                    {STRINGS.SAMPLE_GLYPH}
                  </span>
                  <div className="m-font-selector__title-row">
                    <div className="m-font-selector__title-block">
                      <Typography
                        className="m-font-selector__title"
                        style={{ fontFamily: getFontStack(font.value) }}
                      >
                        {font.label}
                      </Typography>
                      <Typography className="m-font-selector__meta">
                        {font.category}
                      </Typography>
                    </div>
                  </div>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </div>

      {DISPLAY_SETTINGS.map((setting) => (
        <div className="m-display__section" key={setting.key}>
          <div className="m-display__section-head">
            <Typography className="m-display__section-title">
              {setting.label}
            </Typography>
            <Typography className="m-display__section-desc">
              {setting.description}
            </Typography>
          </div>
          <div
            className="m-display__options"
            role="radiogroup"
            aria-label={setting.label}
          >
            {setting.options.map((option) => {
              const isActive = displayValues[setting.key] === option.value;
              const optionClassName =
                "m-display__option" +
                (isActive ? " m-display__option--active" : "");
              return (
                <button
                  type="button"
                  key={option.value}
                  className={optionClassName}
                  onClick={() => handleSelectDisplay(setting.key, option.value)}
                  role="radio"
                  aria-checked={isActive}
                  style={optionPreviewStyle(setting.key, option.value)}
                >
                  <span className="m-display__option-label">
                    {option.label}
                  </span>
                  {option.hint ? (
                    <span className="m-display__option-hint">{option.hint}</span>
                  ) : null}
                  {isActive ? (
                    <CheckCircleIcon
                      className="m-display__option-check"
                      fontSize="small"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

M_FontSelector.propTypes = {
  onFontChange: PropTypes.func,
};

export default M_FontSelector;
